import { useEffect, useMemo, useState } from 'react';

import { logError } from '@/lib/sentry.lib';
import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Loading, { LoadingBall } from '@ingka/loading';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import crossCircleIcon from '@ingka/ssr-icon/paths/cross-circle';
import informationCircleIcon from '@ingka/ssr-icon/paths/information-circle';
import { translate } from '@repo/phrase';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';

import type { ActionModalProps } from '../asset-modal.types';

import { buildPickingListPdfData } from './asset-picking-list-pdf-modal.utils';
import styles from './styles.module.scss';

export const AssetPickingListPdfModal = ({ visible, onClose }: ActionModalProps) => {
    const { assetId, assetType } = useAssetParams();
    const { data } = useAsset(assetType, assetId);

    if (!visible) {
        return null;
    }

    return (
        <AssetPickingListPdfModalContent
            key={data ? assetId : 'loading'}
            assetId={assetId}
            data={data}
            onClose={onClose}
        />
    );
};

interface AssetPickingListPdfModalContentProps {
    assetId: string;
    data: Parameters<typeof buildPickingListPdfData>[0] | undefined;
    onClose: () => void;
}

const AssetPickingListPdfModalContent = ({ assetId, data, onClose }: AssetPickingListPdfModalContentProps) => {
    const pickingListPdfData = useMemo(
        () => (data ? buildPickingListPdfData(data, assetId) : undefined),
        [assetId, data]
    );
    const [isPickingListPdfLoading, setIsPickingListPdfLoading] = useState(Boolean(pickingListPdfData));
    const [pickingListPdfUrl, setPickingListPdfUrl] = useState<string | undefined>();
    const [pickingListPdfMessage, setPickingListPdfMessage] = useState<'empty' | 'error' | undefined>(
        data && !pickingListPdfData ? 'empty' : undefined
    );

    useEffect(() => {
        if (!pickingListPdfData) {
            return;
        }

        let isCancelled = false;
        let generatedUrl: string | undefined;

        const generatePickingListPdf = async () => {
            try {
                const { generatePdf, pdfPresets } = await import('@sp-saljapro/pdf-generator');
                const blob = await generatePdf({
                    ...pdfPresets.pickingListV1,
                    data: pickingListPdfData,
                    metadata: { title: translate('pdf_picking_list_title') },
                });
                const url = URL.createObjectURL(blob);
                generatedUrl = url;

                if (isCancelled) {
                    URL.revokeObjectURL(url);
                    return;
                }

                setPickingListPdfUrl(url);
            } catch (error) {
                if (!isCancelled) {
                    logError(error);
                    setPickingListPdfMessage('error');
                }
            } finally {
                if (!isCancelled) {
                    setIsPickingListPdfLoading(false);
                }
            }
        };

        void generatePickingListPdf();

        return () => {
            isCancelled = true;
            if (generatedUrl) {
                URL.revokeObjectURL(generatedUrl);
            }
        };
    }, [pickingListPdfData]);

    const title = translate('asset_picking_list_modal_header');

    return (
        <Modal
            data-testid="picking-list-pdf-modal"
            visible
            handleCloseBtn={() => {
                setIsPickingListPdfLoading(false);
                setPickingListPdfUrl(undefined);
                setPickingListPdfMessage(undefined);
                onClose();
            }}
        >
            <Sheets
                size="medium"
                aria-label={title}
                header={<ModalHeader title={title} />}
                footer={
                    pickingListPdfUrl ? (
                        <ModalFooter>
                            <Button
                                type="primary"
                                text={translate('asset_download_button')}
                                href={pickingListPdfUrl}
                                download={`picking-list-${assetId}.pdf`}
                                onClick={() => null}
                            />
                        </ModalFooter>
                    ) : null
                }
            >
                <ModalBody>
                    {isPickingListPdfLoading && (
                        <Loading text={translate('page_loading')}>
                            <LoadingBall color="primary" />
                        </Loading>
                    )}
                    {pickingListPdfMessage === 'empty' && (
                        <InlineMessage
                            title={translate('asset_picking_list_modal_empty_title')}
                            body={translate('asset_picking_list_modal_empty_body')}
                            variant="informative"
                            ssrIcon={informationCircleIcon}
                        />
                    )}
                    {pickingListPdfMessage === 'error' && (
                        <InlineMessage
                            title={translate('asset_picking_list_modal_error_title')}
                            body={translate('asset_picking_list_modal_error_body')}
                            variant="negative"
                            ssrIcon={crossCircleIcon}
                        />
                    )}
                    {pickingListPdfUrl && (
                        <iframe className={styles.picking_list_pdf_preview} title={title} src={pickingListPdfUrl} />
                    )}
                </ModalBody>
            </Sheets>
        </Modal>
    );
};
